import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle02Icon } from "@hugeicons-pro/core-solid-rounded";
import moment from "moment";
import { Card } from "shared/components/Card";
import { Button, ButtonVariant } from "shared/components";
import { Applicant, BankAccount, SubmissionType } from "shared/types";
import { ApplicantRoleLabel, formatToCurrency } from "shared/utils";
import FeeDisplay from "shared/components/FeeDisplay";
import { TemplateCosts } from "../../../types";
import { useMemo } from "react";

interface ApplicationFeePaidViewProps {
  applicant: Applicant;
  templateCosts?: TemplateCosts;
  propertyBankAccount?: BankAccount;
  onFinish: () => void;
}

const PaymentMethodLabel = {
  [SubmissionType.CheckoutPaidInCash]: "Paid in-person",
  [SubmissionType.SendByEmail]: "Credit card",
  [SubmissionType.InPerson]: "Applicant’s credit card"
};

export const ApplicationFeePaidView = ({
  applicant,
  templateCosts,
  propertyBankAccount,
  onFinish
}: ApplicationFeePaidViewProps) => {
  const totalCost = useMemo(() => {
    if (applicant.preferred_checkout_type === SubmissionType.CheckoutPaidInCash)
      return templateCosts?.screening_fee
        ? {
            cents: -templateCosts.screening_fee.cents,
            currency: templateCosts.screening_fee.currency
          }
        : null;

    return templateCosts?.partners_profit || templateCosts?.partners_fee;
  }, [applicant.preferred_checkout_type, templateCosts]);

  return (
    <>
      <div className="h-[calc(80vh-153px)] max-w-[460px] mx-auto flex flex-col items-center justify-center gap-6">
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={48}
          className="text-success-500"
        />
        <div className="space-y-2 text-center">
          <h2 className="text-content-500 font-bold">
            Application fee already paid
          </h2>
          <p className="text-sm text-content-300">
            {applicant.application_finished_at
              ? moment(applicant.application_finished_at).format(
                  "MMM D, YYYY [at] h:mm A"
                )
              : "N/A"}
          </p>
        </div>
        <div className="space-y-2 w-full">
          <Card className="p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-content-300">
                Screening cost
              </p>
              <p className="text-sm font-medium">
                {templateCosts?.screening_fee
                  ? formatToCurrency(templateCosts.screening_fee)
                  : "--"}
              </p>
            </div>
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-content-300">
                Application fee
              </p>
              <p className="text-sm font-medium">
                {templateCosts?.application_fee
                  ? formatToCurrency(templateCosts.application_fee)
                  : "--"}
              </p>
            </div>

            <div className="flex items-center justify-between space-y-2">
              <p className="text-sm font-medium text-content-300">Paid by</p>
              <p className="text-sm font-medium">
                {applicant.role ? ApplicantRoleLabel[applicant.role] : "--"}
              </p>
            </div>

            <div className="flex items-center justify-between space-y-2">
              <p className="text-sm font-medium text-content-300">
                Payment method
              </p>
              <p className="text-sm font-medium">
                {applicant.preferred_checkout_type
                  ? PaymentMethodLabel[applicant.preferred_checkout_type]
                  : "--"}
              </p>
            </div>
          </Card>

          <Card className="p-[20px]">
            <div className="space-y-2 pb-4 mb-4 border-b border-alpha-100">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-content-300">Total</p>
                <p className="text-sm font-medium">
                  <FeeDisplay
                    feeInCents={totalCost?.cents}
                    currency={totalCost?.currency}
                    className="text-sm font-bold"
                  />
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-content-300">
                Will be added to the property&apos;s linked bank account
              </p>
              <p className="text-sm font-medium">
                {propertyBankAccount?.account_number ?? "--"}
              </p>
            </div>
          </Card>
        </div>
      </div>
      <div className="mt-auto py-4 px-6 border-t border-alpha-100 gap-2">
        <Button
          variant={ButtonVariant.PRIMARY}
          className="w-[125px] ml-auto"
          onClick={() => onFinish()}
        >
          Finish
        </Button>
      </div>
    </>
  );
};
